/**
 * Image utility functions for extracting and processing meeting images
 */

/**
 * Convert HEIC image URLs to web-compatible format (JPG)
 * 
 * WEB-ONLY: This function is only used in the frontend/web codebase.
 * Mobile apps (iOS/Android) natively support HEIC format, so they don't need conversion.
 * 
 * Web browsers don't support HEIC format, so we convert HEIC URLs to JPG.
 * This assumes that when HEIC images are uploaded, a JPG version is also stored
 * in GCP with the same filename but .jpg extension.
 * 
 * @param {string} imageUrl - Image URL that may be in HEIC format
 * @returns {string} Converted image URL (JPG) or original URL if not HEIC
 */
export const convertHeicToWebFormat = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl;
  }
  
  // Only convert HEIC files (web browsers can't display them)
  // Mobile apps can handle HEIC natively, so they use the original URL
  if (/\.heic$/i.test(imageUrl)) {
    // Replace .heic with .jpg (assumes JPG version exists in GCP)
    return imageUrl.replace(/\.heic$/i, '.jpg');
  }
  
  return imageUrl;
};

/**
 * Return the card-sized image URL for list/homepage views.
 * Backend uploads a _card.jpg variant for meeting_image, clubcard_images, runcard_image.
 *
 * @param {string} imageUrl - Full-size image URL (e.g. .../uuid.jpg)
 * @returns {string} Same URL with _card before extension (e.g. .../uuid_card.jpg)
 */
export const getCardImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl;
  return imageUrl.replace(/\.[a-z0-9]+$/i, '_card.jpg');
};

/**
 * URL to use for card image: card-sized when useCardSize, otherwise full-size.
 *
 * @param {string} imageUrl - Full-size image URL
 * @param {boolean} useCardSize - Whether to request card variant
 * @returns {string} URL to load
 */
export const getDisplayImageUrl = (imageUrl, useCardSize) => {
  if (!imageUrl) return imageUrl;
  return useCardSize ? getCardImageUrl(imageUrl) : imageUrl;
};

/**
 * Props for <img> loading/priority (used by MeetingCard and ClubCard).
 * Uses lowercase fetchpriority so React passes it through to the DOM.
 *
 * @param {boolean} priority - Whether this image is the visible carousel slide
 * @returns {{ loading: 'eager'|'lazy', fetchpriority?: 'high' }}
 */
export const getImageLoadingProps = (priority) => ({
  loading: priority ? 'eager' : 'lazy',
  ...(priority && { fetchpriority: 'high' }),
});

/**
 * Extract the primary image URL from a meeting's images array
 * @param {Object|Array} images - Images array or meeting object with images property
 * @param {string} fallbackUrl - Optional fallback URL if no images found
 * @returns {string|null} Primary image URL or null (converted to web format if HEIC)
 */
export const getPrimaryImageUrl = (images, fallbackUrl = null) => {
  // Handle meeting object with images property
  const imagesArray = Array.isArray(images) ? images : images?.images;
  
  if (!imagesArray || !Array.isArray(imagesArray) || imagesArray.length === 0) {
    return fallbackUrl ? convertHeicToWebFormat(fallbackUrl) : null;
  }
  
  // Find primary image, or use first image
  const primaryImage = imagesArray.find(img => img.is_primary) || imagesArray[0];
  
  // Handle null/undefined primaryImage
  if (!primaryImage) {
    return fallbackUrl ? convertHeicToWebFormat(fallbackUrl) : null;
  }
  
  // Extract URL from image object (supports both url and uri properties)
  // If primaryImage is a string, return it directly
  let imageUrl;
  if (typeof primaryImage === 'string') {
    imageUrl = primaryImage;
  } else {
    // If it's an object, extract url or uri property
    imageUrl = primaryImage.url || primaryImage.uri || fallbackUrl || null;
  }
  
  // Convert HEIC to web-compatible format
  return imageUrl ? convertHeicToWebFormat(imageUrl) : null;
};

/**
 * Extract all image URLs from a meeting's images array
 * @param {Object|Array} images - Images array or meeting object with images property
 * @returns {Array<string>} Array of image URLs (converted to web format if HEIC)
 */
export const getAllImageUrls = (images) => {
  const imagesArray = Array.isArray(images) ? images : images?.images;
  
  if (!imagesArray || !Array.isArray(imagesArray)) {
    return [];
  }
  
  return imagesArray
    .map(img => {
      let imageUrl;
      if (typeof img === 'string') {
        imageUrl = img;
      } else {
        imageUrl = img?.url || img?.uri || null;
      }
      // Convert HEIC to web-compatible format
      return imageUrl ? convertHeicToWebFormat(imageUrl) : null;
    })
    .filter(Boolean);
};

